import React, { useState, useEffect } from 'react';
import { 
  ShieldCheck, 
  Activity,
  Play,
  Pause
} from 'lucide-react';

interface NodeDetail {
  id: string;
  name: string;
  category: 'Sources' | 'Collector' | 'Staging' | 'Core' | 'Serving' | 'Presentation';
  typeLabel: string;
  summary: string;
  techSpecs: {
    protocol: string;
    security: string;
    throughput: string;
    behavior: string;
  };
  connectedTo: string[];
}

const NODE_DETAILS: Record<string, NodeDetail> = {
  sources: {
    id: 'sources',
    name: 'Multi-Vendor Network Gear',
    category: 'Sources',
    typeLabel: 'PHYSICAL & VIRTUAL EDGE',
    summary: 'Mixed-vendor access, aggregation, and core infrastructure across Cisco, Calix, Nokia, Adtran, Juniper, and MikroTik (DOCSIS, PON, wireless, and IP routing).',
    techSpecs: {
      protocol: 'SSH / CLI Polling, Streaming Telemetry, SNMP (Fallback), REST APIs',
      security: 'Isolated Management VLAN (Never touched by public internet)',
      throughput: 'Low-impact adaptive polling (Zero CPU spikes on OLTs or CMTS)',
      behavior: 'Pulls rich metrics without requiring custom on-device vendor agents'
    },
    connectedTo: ['collector']
  },
  collector: {
    id: 'collector',
    name: 'MUSE Collector (P1)',
    category: 'Collector',
    typeLabel: 'LIGHTWEIGHT ON-PREM AGENT',
    summary: 'Lightweight container or micro-appliance behind your firewall. Executes CPU-conscious polling and locally spools data during WAN link drops.',
    techSpecs: {
      protocol: 'Outbound-only TLS 1.3 / gRPC streaming tunnel',
      security: 'Zero open inbound firewall ports; no public IP required; LUKS-encrypted disk',
      throughput: '10,000+ metrics/sec on minimal VM compute (1-2 vCPUs)',
      behavior: 'Store-and-forward spooling ensures zero data loss during upstream fiber cuts'
    },
    connectedTo: ['sources', 'bucket']
  },
  bucket: {
    id: 'bucket',
    name: 'Raw Ingestion Bucket',
    category: 'Staging',
    typeLabel: 'SINGLE-TENANT LANDING ZONE',
    summary: 'Dedicated cloud landing zone for raw telemetry bundles. Creates an immutable operational audit log and triggers real-time stream normalization.',
    techSpecs: {
      protocol: 'Cloud Event Triggers / HTTPS PUT',
      security: '100% Single-tenant dedicated cloud project (Zero data co-mingling)',
      throughput: 'Instant line-rate ingestion with zero backpressure',
      behavior: 'Dispatches real-time event triggers directly to the Ingestion Engine'
    },
    connectedTo: ['collector', 'engester']
  },
  engester: {
    id: 'engester',
    name: 'MUSE Forge & Ingestion Engine',
    category: 'Staging',
    typeLabel: 'AI-ASSISTED NORMALIZATION',
    summary: 'Transforms unstructured terminal CLI outputs, logs, and vendor streams into standardized telemetry schemas in under 60 seconds via MUSE Forge.',
    techSpecs: {
      protocol: 'MUSE Forge Auto-Parser & Serverless Pipeline',
      security: 'Isolated VPC execution; strict per-ISP data boundary',
      throughput: 'Sub-50ms stream normalization latency',
      behavior: 'Dual-path routing: streams to live Event Engine while committing to Data Lake'
    },
    connectedTo: ['bucket', 'datalake', 'eventEngine']
  },
  datalake: {
    id: 'datalake',
    name: 'Distributed Data Lake',
    category: 'Core',
    typeLabel: 'TIME-SERIES & TOPOLOGY LAKE',
    summary: 'Single-tenant analytical repository storing 1.5 to 2 years of high-resolution telemetry, configuration diff histories, and physical-to-geographic topology maps.',
    techSpecs: {
      protocol: 'Columnar Parquet / Distributed SQL Analytical Layer',
      security: 'Dedicated cloud project per ISP, AES-256 encryption at rest',
      throughput: '1.5+ years of retention for seasonal & weather trend analysis',
      behavior: 'Supplies topology dependency models to Event Engine & API'
    },
    connectedTo: ['engester', 'eventEngine', 'api']
  },
  eventEngine: {
    id: 'eventEngine',
    name: 'Intelligent Event Clustering Core',
    category: 'Core',
    typeLabel: 'REAL-TIME ROOT-CAUSE CORRELATION',
    summary: 'The correlation brain of MUSE Telemetry. Cross-references physical topology with subscriber geolocation to collapse 200+ cascading alarms into 1 actionable incident.',
    techSpecs: {
      protocol: 'In-Memory Graph & Topology Correlation Engine',
      security: 'Dedicated single-tenant execution memory space',
      throughput: 'Sub-100ms alarm storm suppression and root-cause pinpointing',
      behavior: 'Suppresses downstream subscriber noise to isolate the exact cut line, card, or port'
    },
    connectedTo: ['engester', 'datalake', 'api']
  },
  cache: {
    id: 'cache',
    name: 'In-Memory State Accelerator',
    category: 'Serving',
    typeLabel: 'LOW-LATENCY CACHE',
    summary: 'High-speed in-memory store keeping live node reachability, current metric baselines, and active incident clusters primed for instant NOC display.',
    techSpecs: {
      protocol: 'Redis-compatible High-Speed Memory Protocol',
      security: 'Private VPC memory cluster, TLS in-transit encryption',
      throughput: '<2ms query response for live operational status lookups',
      behavior: 'Keeps live device status and active clusters instantly ready for UI push'
    },
    connectedTo: ['api']
  },
  api: {
    id: 'api',
    name: 'Unified API & Integration Gateway',
    category: 'Serving',
    typeLabel: 'STREAMING & INTEGRATION LAYER',
    summary: 'Unified gateway feeding live push streams to the NOC UI and dispatching automated incident webhooks to your existing ticketing systems.',
    techSpecs: {
      protocol: 'Secure WebSockets (WSS), REST APIs, Outbound Webhooks',
      security: 'Role-Based Access Control (RBAC), MFA, granular audit logging',
      throughput: 'Instant webhook dispatch to Slack, PagerDuty, or billing systems',
      behavior: 'Pushes live telemetry to browser sessions without continuous polling'
    },
    connectedTo: ['datalake', 'eventEngine', 'cache', 'webui']
  },
  webui: {
    id: 'webui',
    name: 'NOC Command & Web UI',
    category: 'Presentation',
    typeLabel: 'SINGLE PANE OF GLASS',
    summary: 'Clean, responsive command interface for NOC engineers, operations leads, and field techs. Includes topology explorer, config diffs, and incident clusters.',
    techSpecs: {
      protocol: 'Push-based WebSockets (WSS) & Modern SPA',
      security: 'Encrypted browser session; zero device credentials exposed to client',
      throughput: 'Push-based updates (no manual browser refreshes required)',
      behavior: 'Interactive root-cause drill-downs, live config diffs, and outage heatmaps'
    },
    connectedTo: ['api']
  }
};

const CAROUSEL_SEQUENCE = [
  'sources',
  'collector',
  'bucket',
  'engester',
  'datalake',
  'eventEngine',
  'api',
  'webui'
];

export const PlatformArchitectureVisualizer: React.FC = () => {
  const [activeNodeId, setActiveNodeId] = useState<string>('sources');
  const [viewMode, setViewMode] = useState<'pipeline' | 'security'>('pipeline');
  const [isAutoPlaying, setIsAutoPlaying] = useState<boolean>(true);

  // Rolling carousel glow cycle across pipeline nodes
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setActiveNodeId((current) => {
        const currentIndex = CAROUSEL_SEQUENCE.indexOf(current);
        const nextIndex = (currentIndex + 1) % CAROUSEL_SEQUENCE.length;
        return CAROUSEL_SEQUENCE[nextIndex];
      });
    }, 2800);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const handleNodeClick = (nodeId: string) => {
    setActiveNodeId(nodeId);
    setIsAutoPlaying(false);
  };

  const activeNode = NODE_DETAILS[activeNodeId] || NODE_DETAILS.sources;

  return (
    <div className="flat-card arch-visualizer-card">
      {/* Top Header Row */}
      <div className="arch-top-head">
        <div>
          <div className="pill-tag mb-2">
            <span className="status-dot blue" />
            <span>PLATFORM ARCHITECTURE &amp; PIPELINE</span>
          </div>
          <h3>Single-Tenant Cloud Pipeline: Ingress to Observability</h3>
          <p className="subtext">
            Trace telemetry flow from on-premises edge collectors through single-tenant cloud ingestion, real-time correlation, and the NOC UI.
          </p>
        </div>

        <div className="view-mode-toggle">
          <button
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            className={`mode-btn ${isAutoPlaying ? 'active' : ''}`}
            title={isAutoPlaying ? 'Pause rolling tour' : 'Start rolling tour'}
          >
            {isAutoPlaying ? <Pause size={13} /> : <Play size={13} />}
            <span>{isAutoPlaying ? 'Rolling Tour' : 'Paused'}</span>
          </button>
          <button
            onClick={() => setViewMode('pipeline')}
            className={`mode-btn ${viewMode === 'pipeline' ? 'active' : ''}`}
          >
            <Activity size={14} />
            <span>Pipeline Flow</span>
          </button>
          <button
            onClick={() => setViewMode('security')}
            className={`mode-btn ${viewMode === 'security' ? 'active' : ''}`}
          >
            <ShieldCheck size={14} />
            <span>Security &amp; Boundaries</span>
          </button>
        </div>
      </div>

      {/* Main Interactive Diagram Canvas */}
      <div className="arch-canvas-wrap">
        <div className="canvas-banner">
          <div className="banner-left">
            <span className={`status-dot ${viewMode === 'pipeline' ? 'green' : 'blue'}`} />
            {viewMode === 'pipeline' ? (
              <span><strong>Active Live Stream:</strong> Hardware telemetry auto-ingested, normalized, and clustered in sub-second cycles.</span>
            ) : (
              <span><strong>Zero-Trust Boundary:</strong> Collector operates 100% outbound-only. Cloud backend runs in a dedicated single-tenant project.</span>
            )}
          </div>
          <span className="banner-hint font-mono">
            {isAutoPlaying ? 'Rolling auto-tour active — Click any node to freeze inspection' : 'Click any component to inspect technical specifications'}
          </span>
        </div>

        <div className="svg-container">
          <svg className="arch-svg" viewBox="0 0 1120 420" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              {/* Subtle Canvas Grid */}
              <pattern id="arch-grid" width="30" height="30" patternUnits="userSpaceOnUse">
                <path d="M 30 0 L 0 0 0 30" fill="none" stroke="#141414" strokeWidth="1" />
              </pattern>

              {/* Arrow Markers */}
              <marker id="arrow-green" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#10b981" />
              </marker>
              <marker id="arrow-blue" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#3b82f6" />
              </marker>
              <marker id="arrow-amber" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#f59e0b" />
              </marker>
              <marker id="arrow-cyan" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#06b6d4" />
              </marker>

              {/* Dynamic Glow Filters for Rolling Active Highlights */}
              <filter id="glow-active-blue" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="0" dy="0" stdDeviation="6" floodColor="#3b82f6" floodOpacity="0.85" />
              </filter>
              <filter id="glow-active-green" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="0" dy="0" stdDeviation="6" floodColor="#10b981" floodOpacity="0.85" />
              </filter>
              <filter id="glow-active-amber" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="0" dy="0" stdDeviation="6" floodColor="#f59e0b" floodOpacity="0.85" />
              </filter>
              <filter id="glow-active-indigo" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="0" dy="0" stdDeviation="6" floodColor="#818cf8" floodOpacity="0.85" />
              </filter>
              <filter id="glow-active-purple" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="0" dy="0" stdDeviation="6" floodColor="#a855f7" floodOpacity="0.85" />
              </filter>
            </defs>

            {/* Background Grid */}
            <rect width="1120" height="420" fill="url(#arch-grid)" />

            {/* ================= BOUNDARY ZONES (In Security Mode) ================= */}
            {viewMode === 'security' && (
              <g className="security-zones">
                {/* On-Premises ISP Boundary */}
                <rect x="20" y="20" width="300" height="380" rx="8" fill="rgba(16, 185, 129, 0.03)" stroke="rgba(16, 185, 129, 0.25)" strokeDasharray="4 4" />
                <text x="170" y="370" fill="#10b981" fontSize="9" fontFamily="var(--font-mono)" fontWeight="700" textAnchor="middle">
                  <tspan x="170" dy="0">ISP ON-PREMISES / PRIVATE LAN</tspan>
                  <tspan x="170" dy="14">(ZERO OPEN INBOUND PORTS)</tspan>
                </text>

                {/* Single-Tenant Cloud Boundary */}
                <rect x="345" y="20" width="755" height="380" rx="8" fill="rgba(59, 130, 246, 0.03)" stroke="rgba(59, 130, 246, 0.25)" strokeDasharray="4 4" />
                <text x="722" y="378" fill="#60a5fa" fontSize="9.5" fontFamily="var(--font-mono)" fontWeight="700" textAnchor="middle">
                  100% SINGLE-TENANT DEDICATED CLOUD PROJECT (AES-256 ISOLATION)
                </text>
              </g>
            )}

            {/* ================= CONNECTION PATHS & ARROWS ================= */}

            {/* 1. Sources <-> Collector (Bidirectional) */}
            <line x1="140" y1="120" x2="210" y2="185" stroke="#3b82f6" strokeWidth="1.5" strokeDasharray="3 3" />
            <line x1="140" y1="180" x2="210" y2="202" stroke="#3b82f6" strokeWidth="1.5" strokeDasharray="3 3" />
            <line x1="140" y1="240" x2="210" y2="220" stroke="#3b82f6" strokeWidth="1.5" strokeDasharray="3 3" />

            {/* 2. Collector -> Raw Bucket (Outbound TLS 1.3) */}
            <path d="M 295 202 L 365 202" stroke="#10b981" strokeWidth="2.5" markerEnd="url(#arrow-green)" />
            {/* Label placed directly above the Collector (P1) node */}
            <text x="252" y="145" fill="#10b981" fontSize="9" fontFamily="var(--font-mono)" fontWeight="700" textAnchor="middle">
              TLS 1.3 Outbound
            </text>

            {/* 3. Bucket -> Ingestion (Trigger) */}
            <path d="M 450 202 L 520 202" stroke="#f59e0b" strokeWidth="2.5" markerEnd="url(#arrow-amber)" />
            {/* Free-floating Trigger label above path */}
            <text x="485" y="185" fill="#f59e0b" fontSize="9" fontFamily="var(--font-mono)" fontWeight="700" textAnchor="middle">
              Trigger
            </text>

            {/* 4. Ingestion -> Data Lake (Normalize & Store) */}
            <path d="M 605 180 L 670 115" stroke="#06b6d4" strokeWidth="2" markerEnd="url(#arrow-cyan)" />

            {/* 5. Ingestion -> Event Engine (Live Stream) */}
            <path d="M 605 225 L 670 290" stroke="#f59e0b" strokeWidth="2" markerEnd="url(#arrow-amber)" />

            {/* 6. Data Lake <-> Event Engine (Bidirectional Topology & States) */}
            <path d="M 700 150 C 675 190, 675 215, 700 255" stroke="#60a5fa" strokeWidth="1.5" markerEnd="url(#arrow-blue)" fill="none" />
            <path d="M 745 255 C 770 215, 770 190, 745 150" stroke="#60a5fa" strokeWidth="1.5" markerEnd="url(#arrow-blue)" fill="none" />
            {/* Free-floating label centered between Lake & Engine */}
            <text x="722" y="205" fill="#60a5fa" fontSize="8" fontFamily="var(--font-mono)" fontWeight="600" textAnchor="middle">
              Telemetry Query
            </text>

            {/* 7. Data Lake -> API Gateway */}
            <path d="M 775 102 L 845 155" stroke="#06b6d4" strokeWidth="2" markerEnd="url(#arrow-cyan)" />

            {/* 8. Event Engine -> API Gateway (Events Stream) */}
            <path d="M 775 302 L 845 285" stroke="#f59e0b" strokeWidth="2.5" markerEnd="url(#arrow-amber)" />
            {/* Free-floating Events Stream label above path */}
            <text x="810" y="278" fill="#f59e0b" fontSize="8.5" fontFamily="var(--font-mono)" fontWeight="700" textAnchor="middle">
              Events Stream
            </text>

            {/* 9. Cache <-> API Gateway */}
            <path d="M 867 95 L 867 125" stroke="#a855f7" strokeWidth="1.5" strokeDasharray="3 3" />
            {/* Free-floating Sub-ms Sync label at top of Cache */}
            <text x="867" y="30" fill="#c084fc" fontSize="8" fontFamily="var(--font-mono)" fontWeight="600" textAnchor="middle">
              Sub-ms Sync
            </text>

            {/* 10. API Gateway <-> Web UI (Bidirectional WSS / REST) */}
            <path d="M 890 195 L 955 195" stroke="#10b981" strokeWidth="2.5" markerEnd="url(#arrow-green)" />
            <path d="M 955 235 L 890 235" stroke="#60a5fa" strokeWidth="2" markerEnd="url(#arrow-blue)" />
            {/* Free-floating WSS & REST labels */}
            <text x="922" y="186" fill="#10b981" fontSize="8" fontFamily="var(--font-mono)" fontWeight="700" textAnchor="middle">
              WSS (Live)
            </text>
            <text x="922" y="252" fill="#60a5fa" fontSize="8" fontFamily="var(--font-mono)" fontWeight="700" textAnchor="middle">
              REST (Query)
            </text>

            {/* ================= COMPONENT NODES ================= */}

            {/* 1. SOURCES GROUP */}
            <g className="clickable-node" onClick={() => handleNodeClick('sources')}>
              <rect 
                x="30" y="65" width="110" height="280" rx="6" 
                fill="#0a0a0a" 
                stroke={activeNodeId === 'sources' ? '#3b82f6' : '#262626'} 
                strokeWidth={activeNodeId === 'sources' ? 2.5 : 1}
                filter={activeNodeId === 'sources' ? 'url(#glow-active-blue)' : undefined}
              />
              <text x="85" y="86" fill="#ffffff" fontSize="9.5" fontFamily="var(--font-mono)" fontWeight="700" textAnchor="middle">SOURCES</text>

              {/* Sub-node: Cisco */}
              <rect x="40" y="98" width="90" height="48" rx="4" fill="#141414" stroke="#333333" />
              <text x="85" y="119" fill="#f1f5f9" fontSize="9.5" fontFamily="var(--font-mono)" fontWeight="600" textAnchor="middle">Cisco</text>
              <text x="85" y="135" fill="#94a3b8" fontSize="7.5" fontFamily="var(--font-mono)" textAnchor="middle">IOS-XE / XR</text>

              {/* Sub-node: Calix */}
              <rect x="40" y="158" width="90" height="48" rx="4" fill="#141414" stroke="#333333" />
              <text x="85" y="179" fill="#f1f5f9" fontSize="9.5" fontFamily="var(--font-mono)" fontWeight="600" textAnchor="middle">Calix</text>
              <text x="85" y="195" fill="#94a3b8" fontSize="7.5" fontFamily="var(--font-mono)" textAnchor="middle">AXOS / E7</text>

              {/* Sub-node: Nokia */}
              <rect x="40" y="218" width="90" height="48" rx="4" fill="#141414" stroke="#333333" />
              <text x="85" y="239" fill="#f1f5f9" fontSize="9.5" fontFamily="var(--font-mono)" fontWeight="600" textAnchor="middle">Nokia</text>
              <text x="85" y="255" fill="#94a3b8" fontSize="7.5" fontFamily="var(--font-mono)" textAnchor="middle">ISAM / 7360</text>

              <text x="85" y="325" fill="#60a5fa" fontSize="8" fontFamily="var(--font-mono)" textAnchor="middle">SSH / SNMP / API</text>
            </g>

            {/* 2. COLLECTOR (P1) */}
            <g className="clickable-node" onClick={() => handleNodeClick('collector')}>
              <rect 
                x="210" y="155" width="85" height="95" rx="8" 
                fill="#041f18" 
                stroke={activeNodeId === 'collector' ? '#10b981' : '#059669'} 
                strokeWidth={activeNodeId === 'collector' ? 2.5 : 1.5}
                filter={activeNodeId === 'collector' ? 'url(#glow-active-green)' : undefined}
              />
              {/* P1 Microcontroller Chip Icon */}
              <g transform="translate(229, 158) scale(0.58)">
                {/* Pins */}
                <path d="M20 10v4M30 10v4M40 10v4M50 10v4M60 10v4M20 66v4M30 66v4M40 66v4M50 66v4M60 66v4" stroke="#10b981" strokeWidth="2" strokeLinecap="round" />
                <path d="M10 20h4M10 30h4M10 40h4M10 50h4M10 60h4M66 20h4M66 30h4M66 40h4M66 50h4M66 60h4" stroke="#10b981" strokeWidth="2" strokeLinecap="round" />
                {/* Chip Body */}
                <rect x="16" y="16" width="48" height="48" rx="6" fill="#042018" stroke="#10b981" strokeWidth="2" />
                <rect x="24" y="24" width="32" height="32" rx="4" fill="#059669" fillOpacity="0.2" stroke="#34d399" strokeDasharray="2 2" />
                <text x="40" y="45" fill="#34d399" fontSize="14" fontWeight="700" fontFamily="var(--font-mono)" textAnchor="middle">P1</text>
                {/* Activity Indicator */}
                <circle cx="21" cy="21" r="2.5" fill="#6ee7b7" />
              </g>
              <text x="252" y="222" fill="#ffffff" fontSize="9.5" fontFamily="var(--font-mono)" fontWeight="600" textAnchor="middle">Collector</text>
              <text x="252" y="236" fill="#6ee7b7" fontSize="7.5" fontFamily="var(--font-mono)" textAnchor="middle">On-Prem Agent</text>
            </g>

            {/* 3. RAW DATA BUCKET */}
            <g className="clickable-node" onClick={() => handleNodeClick('bucket')}>
              <rect 
                x="365" y="155" width="85" height="95" rx="8" 
                fill="#0b192c" 
                stroke={activeNodeId === 'bucket' ? '#3b82f6' : '#1e3a8a'} 
                strokeWidth={activeNodeId === 'bucket' ? 2.5 : 1.5}
                filter={activeNodeId === 'bucket' ? 'url(#glow-active-blue)' : undefined}
              />
              {/* Cylinder with Data Matrix Grid Icon */}
              <g transform="translate(385, 158) scale(0.65)">
                {/* Main Storage Cylinder */}
                <ellipse cx="32" cy="18" rx="20" ry="7.5" fill="#0f172a" stroke="#38bdf8" strokeWidth="2" />
                <path d="M 12 18 v 14 a 20 7.5 0 0 0 40 0 v -14" fill="#090d16" stroke="#38bdf8" strokeWidth="2" />
                <path d="M 12 32 v 16 a 20 7.5 0 0 0 20 7.3" fill="#090d16" stroke="#38bdf8" strokeWidth="2" />
                <path d="M 12 32 a 20 7.5 0 0 0 22 7.2" stroke="#0ea5e9" strokeWidth="1.5" fill="none" />

                {/* Overlaid 3x3 Data Matrix Grid */}
                <rect x="28" y="28" width="30" height="30" rx="4" fill="#090d16" stroke="#38bdf8" strokeWidth="1.75" />
                {/* Row 1 */}
                <rect x="32" y="32" width="6" height="6" rx="1" fill="#0ea5e9" fillOpacity="0.75" />
                <rect x="40" y="32" width="6" height="6" rx="1" fill="#38bdf8" fillOpacity="0.9" />
                <rect x="48" y="32" width="6" height="6" rx="1" fill="#93c5fd" fillOpacity="0.7" />
                {/* Row 2 */}
                <rect x="32" y="40" width="6" height="6" rx="1" fill="#38bdf8" fillOpacity="0.85" />
                <rect x="40" y="40" width="6" height="6" rx="1" fill="#ffffff" fillOpacity="0.95" />
                <rect x="48" y="40" width="6" height="6" rx="1" fill="#0284c7" fillOpacity="0.8" />
                {/* Row 3 */}
                <rect x="32" y="48" width="6" height="6" rx="1" fill="#0284c7" fillOpacity="0.65" />
                <rect x="40" y="48" width="6" height="6" rx="1" fill="#0ea5e9" fillOpacity="0.85" />
                <rect x="48" y="48" width="6" height="6" rx="1" fill="#38bdf8" fillOpacity="0.95" />
              </g>
              <text x="407" y="222" fill="#ffffff" fontSize="9.5" fontFamily="var(--font-mono)" fontWeight="600" textAnchor="middle">Raw Bucket</text>
              <text x="407" y="236" fill="#93c5fd" fontSize="7.5" fontFamily="var(--font-mono)" textAnchor="middle">Landing Zone</text>
            </g>

            {/* 4. INGESTION ENGINE */}
            <g className="clickable-node" onClick={() => handleNodeClick('engester')}>
              <rect 
                x="520" y="155" width="85" height="95" rx="8" 
                fill="#022c22" 
                stroke={activeNodeId === 'engester' ? '#10b981' : '#059669'} 
                strokeWidth={activeNodeId === 'engester' ? 2.5 : 1.5}
                filter={activeNodeId === 'engester' ? 'url(#glow-active-green)' : undefined}
              />
              {/* Document Funnel Ingestion Icon */}
              <g transform="translate(540, 156) scale(0.65)">
                {/* Back Document */}
                <path d="M 28 8 h 20 l 8 8 v 16 h -28 Z" fill="#042018" stroke="#10b981" strokeWidth="1.5" />
                <path d="M 48 8 v 8 h 8" stroke="#34d399" strokeWidth="1.5" fill="none" />
                <line x1="33" y1="18" x2="48" y2="18" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="33" y1="23" x2="45" y2="23" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" />

                {/* Front Document */}
                <path d="M 14 16 h 18 l 7 7 v 14 h -25 Z" fill="#064e3b" stroke="#34d399" strokeWidth="1.5" />
                <path d="M 32 16 v 7 h 7" stroke="#6ee7b7" strokeWidth="1.5" fill="none" />
                <line x1="18" y1="24" x2="30" y2="24" stroke="#6ee7b7" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="18" y1="29" x2="28" y2="29" stroke="#6ee7b7" strokeWidth="1.5" strokeLinecap="round" />

                {/* Funnel / Normalizer Rim & Cone */}
                <rect x="10" y="34" width="50" height="5" rx="1.5" fill="#042018" stroke="#10b981" strokeWidth="1.5" />
                <path d="M 13 39 L 30 54 v 9 h 10 v -9 L 57 39 Z" fill="#042018" stroke="#10b981" strokeWidth="1.5" strokeLinejoin="round" />

                {/* Stream Output Arrows */}
                <path d="M 6 46 v 14 l -4 -4 M 6 60 l 4 -4" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="6" cy="42" r="1.5" fill="#6ee7b7" />
                <circle cx="10" cy="48" r="1" fill="#10b981" />

                <path d="M 64 46 v 14 l -4 -4 M 64 60 l 4 -4" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="64" cy="42" r="1.5" fill="#6ee7b7" />
                <circle cx="60" cy="48" r="1" fill="#10b981" />
              </g>
              <text x="562" y="222" fill="#ffffff" fontSize="9.5" fontFamily="var(--font-mono)" fontWeight="600" textAnchor="middle">Ingestion</text>
              <text x="562" y="236" fill="#6ee7b7" fontSize="7.5" fontFamily="var(--font-mono)" textAnchor="middle">Normalizer</text>
            </g>

            {/* 5. DISTRIBUTED DATA LAKE */}
            <g className="clickable-node" onClick={() => handleNodeClick('datalake')}>
              <rect 
                x="670" y="52" width="105" height="100" rx="8" 
                fill="#0b192c" 
                stroke={activeNodeId === 'datalake' ? '#3b82f6' : '#1e3a8a'} 
                strokeWidth={activeNodeId === 'datalake' ? 2.5 : 1.5}
                filter={activeNodeId === 'datalake' ? 'url(#glow-active-blue)' : undefined}
              />
              {/* Distributed 3-Node Cluster Icon */}
              <g transform="translate(696, 54) scale(0.65)">
                {/* Node Left */}
                <ellipse cx="28" cy="30" rx="10" ry="4" fill="#0f172a" stroke="#0ea5e9" strokeWidth="1.5" />
                <path d="M18 30v14c0 2.2 4.5 4 10 4s10-1.8 10-4V30" fill="#090d16" stroke="#0ea5e9" strokeWidth="1.5" />
                <path d="M18 37c0 2.2 4.5 4 10 4s10-1.8 10-4" stroke="#0284c7" strokeWidth="1" />
                
                {/* Node Right */}
                <ellipse cx="52" cy="30" rx="10" ry="4" fill="#0f172a" stroke="#0ea5e9" strokeWidth="1.5" />
                <path d="M42 30v14c0 2.2 4.5 4 10 4s10-1.8 10-4V30" fill="#090d16" stroke="#0ea5e9" strokeWidth="1.5" />
                <path d="M42 37c0 2.2 4.5 4 10 4s10-1.8 10-4" stroke="#0284c7" strokeWidth="1" />
                
                {/* Node Foreground / Primary */}
                <ellipse cx="40" cy="46" rx="13" ry="5" fill="#0f172a" stroke="#38bdf8" strokeWidth="1.5" />
                <path d="M27 46v14c0 2.8 5.8 5 13 5s13-2.2 13-5V46" fill="#090d16" stroke="#38bdf8" strokeWidth="1.5" />
                <path d="M27 53c0 2.8 5.8 5 13 5s13-2.2 13-5" stroke="#38bdf8" strokeWidth="1" />
              </g>
              <text x="722" y="125" fill="#ffffff" fontSize="9.5" fontFamily="var(--font-mono)" fontWeight="600" textAnchor="middle">Data Lake</text>
              <text x="722" y="139" fill="#7dd3fc" fontSize="7.5" fontFamily="var(--font-mono)" textAnchor="middle">Time-Series &amp; Graph</text>
            </g>

            {/* 6. EVENT PROCESSING ENGINE */}
            <g className="clickable-node" onClick={() => handleNodeClick('eventEngine')}>
              <rect 
                x="670" y="255" width="105" height="95" rx="8" 
                fill="#2b1800" 
                stroke={activeNodeId === 'eventEngine' ? '#f59e0b' : '#b45309'} 
                strokeWidth={activeNodeId === 'eventEngine' ? 2.5 : 1.5}
                filter={activeNodeId === 'eventEngine' ? 'url(#glow-active-amber)' : undefined}
              />
              {/* Regular Hexagon Chamber with Ingress Arrow Logo */}
              <g transform="translate(698, 256) scale(0.65)">
                {/* Hexagon Chamber (Open on the left) */}
                <path 
                  d="M 22 25 L 22 20 L 42 8 L 62 20 L 62 44 L 42 56 L 22 44 L 22 39" 
                  fill="none" 
                  stroke="#f59e0b" 
                  strokeWidth="5.5" 
                  strokeLinecap="butt" 
                  strokeLinejoin="miter" 
                />
                {/* Ingress Event Arrow Shaft */}
                <line 
                  x1="4" 
                  y1="32" 
                  x2="36" 
                  y2="32" 
                  stroke="#fbbf24" 
                  strokeWidth="5.5" 
                  strokeLinecap="square" 
                />
                {/* Arrow Head Chevron */}
                <path 
                  d="M 27 17 L 42 32 L 27 47" 
                  fill="none" 
                  stroke="#fbbf24" 
                  strokeWidth="5.5" 
                  strokeLinecap="square" 
                  strokeLinejoin="miter" 
                />
              </g>
              <text x="722" y="322" fill="#ffffff" fontSize="9.5" fontFamily="var(--font-mono)" fontWeight="600" textAnchor="middle">Event Engine</text>
              <text x="722" y="337" fill="#fde68a" fontSize="7.5" fontFamily="var(--font-mono)" textAnchor="middle">Clustering Core</text>
            </g>

            {/* 7. CACHE SERVICE */}
            <g className="clickable-node" onClick={() => handleNodeClick('cache')}>
              <rect 
                x="825" y="40" width="85" height="55" rx="6" 
                fill="#1f1035" 
                stroke={activeNodeId === 'cache' ? '#a855f7' : '#6b21a8'} 
                strokeWidth={activeNodeId === 'cache' ? 2.5 : 1.5}
                filter={activeNodeId === 'cache' ? 'url(#glow-active-purple)' : undefined}
              />
              <text x="867" y="64" fill="#ffffff" fontSize="9" fontFamily="var(--font-mono)" fontWeight="600" textAnchor="middle">Cache</text>
              <text x="867" y="78" fill="#d8b4fe" fontSize="7.5" fontFamily="var(--font-mono)" textAnchor="middle">In-Memory</text>
            </g>

            {/* 8. API GATEWAY / BACKEND (Vertical Gateway Bar) */}
            <g className="clickable-node" onClick={() => handleNodeClick('api')}>
              <rect 
                x="845" y="125" width="45" height="235" rx="8" 
                fill="#022c22" 
                stroke={activeNodeId === 'api' ? '#10b981' : '#059669'} 
                strokeWidth={activeNodeId === 'api' ? 2.5 : 1.5}
                filter={activeNodeId === 'api' ? 'url(#glow-active-green)' : undefined}
              />
              <text 
                x="867" y="242" 
                fill="#ffffff" 
                fontSize="11" 
                fontFamily="var(--font-mono)" 
                fontWeight="700" 
                letterSpacing="0.1em"
                textAnchor="middle" 
                transform="rotate(-90 867 242)"
              >
                API GATEWAY
              </text>
            </g>

            {/* 9. PRESENTATION (WEB UI DASHBOARD) */}
            <g className="clickable-node" onClick={() => handleNodeClick('webui')}>
              <rect 
                x="955" y="165" width="100" height="115" rx="8" 
                fill="#1e1b4b" 
                stroke={activeNodeId === 'webui' ? '#818cf8' : '#4f46e5'} 
                strokeWidth={activeNodeId === 'webui' ? 2.5 : 1.5}
                filter={activeNodeId === 'webui' ? 'url(#glow-active-indigo)' : undefined}
              />
              {/* Browser window representation */}
              <rect x="968" y="180" width="74" height="48" rx="3" fill="#0f172a" stroke="#6366f1" strokeWidth="1" />
              <circle cx="976" cy="188" r="2" fill="#ef4444" />
              <circle cx="982" cy="188" r="2" fill="#f59e0b" />
              <circle cx="988" cy="188" r="2" fill="#10b981" />
              <rect x="975" y="196" width="26" height="24" fill="#1e293b" />
              <rect x="1007" y="196" width="28" height="8" fill="#10b981" />
              <rect x="1007" y="208" width="28" height="12" fill="#3b82f6" />
              <text x="1005" y="252" fill="#ffffff" fontSize="9.5" fontFamily="var(--font-mono)" fontWeight="600" textAnchor="middle">Web UI</text>
              <text x="1005" y="267" fill="#c7d2fe" fontSize="7.5" fontFamily="var(--font-mono)" textAnchor="middle">NOC Command</text>
            </g>
          </svg>
        </div>

        {/* Legend Bar */}
        <div className="canvas-footer-legend">
          <div className="l-item"><span className="status-dot edge-dot" /> Edge Sources</div>
          <div className="l-item"><span className="status-dot green" /> Compute &amp; Engines</div>
          <div className="l-item"><span className="status-dot blue" /> Single-Tenant Storage &amp; Lake</div>
          <div className="l-item"><span className="status-dot purple" /> In-Memory Cache</div>
          <div className="l-item"><span className="status-dot indigo-dot" /> Web Presentation</div>
        </div>
      </div>

      {/* Selected Component Live Inspector */}
      <div className="node-inspector-panel">
        <div className="inspector-head">
          <div className="inspector-title-wrap">
            <span className="type-badge font-mono">{activeNode.typeLabel}</span>
            <h4>{activeNode.name}</h4>
          </div>
          <span className="inspector-status font-mono">
            <span className="status-dot green" /> ACTIVE SUBSYSTEM
          </span>
        </div>

        <p className="inspector-desc">{activeNode.summary}</p>

        <div className="inspector-specs-grid">
          <div className="spec-box">
            <span className="spec-lbl font-mono">PROTOCOLS / INTERFACE</span>
            <span className="spec-val">{activeNode.techSpecs.protocol}</span>
          </div>

          <div className="spec-box">
            <span className="spec-lbl font-mono">SECURITY &amp; ISOLATION</span>
            <span className="spec-val">{activeNode.techSpecs.security}</span>
          </div>

          <div className="spec-box">
            <span className="spec-lbl font-mono">THROUGHPUT &amp; LATENCY</span>
            <span className="spec-val">{activeNode.techSpecs.throughput}</span>
          </div>

          <div className="spec-box">
            <span className="spec-lbl font-mono">PIPELINE BEHAVIOR</span>
            <span className="spec-val">{activeNode.techSpecs.behavior}</span>
          </div>
        </div>
      </div>

      <style>{`
        .arch-visualizer-card {
          margin: 2rem 0;
        }

        .arch-top-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }

        .arch-top-head h3 {
          font-size: 1.4rem;
          margin-bottom: 0.3rem;
        }

        .view-mode-toggle {
          display: flex;
          gap: 0.4rem;
          background: #050505;
          padding: 0.25rem;
          border-radius: var(--radius-sm);
          border: 1px solid var(--border-color);
        }

        .mode-btn {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          background: transparent;
          border: none;
          color: var(--text-muted);
          font-size: 0.78rem;
          padding: 0.4rem 0.8rem;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .mode-btn:hover {
          color: #ffffff;
        }

        .mode-btn.active {
          background: #ffffff;
          color: #000000;
          font-weight: 600;
        }

        .arch-canvas-wrap {
          background: #050505;
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          overflow: hidden;
        }

        .canvas-banner {
          background: #0d0d0d;
          border-bottom: 1px solid var(--border-color);
          padding: 0.65rem 1rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .banner-left {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          font-size: 0.8rem;
          color: var(--text-muted);
        }

        .banner-left strong {
          color: #ffffff;
        }

        .banner-hint {
          font-size: 0.7rem;
          color: var(--text-dim);
        }

        .svg-container {
          width: 100%;
          overflow-x: auto;
          background: #030303;
          padding: 0.5rem 0;
        }

        .arch-svg {
          width: 100%;
          min-width: 900px;
          height: auto;
          display: block;
        }

        .clickable-node {
          cursor: pointer;
          transition: transform 0.2s ease, opacity 0.2s ease;
        }

        .clickable-node:hover {
          opacity: 0.92;
        }

        .clickable-node rect {
          transition: stroke 0.3s ease, stroke-width 0.3s ease, filter 0.3s ease;
        }

        .canvas-footer-legend {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1.5rem;
          padding: 0.65rem;
          background: #000000;
          border-top: 1px solid var(--border-color);
          font-size: 0.72rem;
          color: var(--text-dim);
          flex-wrap: wrap;
          font-family: var(--font-mono);
        }

        .l-item {
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }

        .edge-dot {
          background: #3b82f6;
          box-shadow: 0 0 6px rgba(59, 130, 246, 0.6);
        }

        .purple {
          background: #a855f7;
          box-shadow: 0 0 6px rgba(168, 85, 247, 0.6);
        }

        .indigo-dot {
          background: #6366f1;
          box-shadow: 0 0 6px rgba(99, 102, 241, 0.6);
        }

        /* Inspector Panel */
        .node-inspector-panel {
          margin-top: 1rem;
          background: #070707;
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          padding: 1.25rem;
        }

        .inspector-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 0.65rem;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .inspector-title-wrap {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .type-badge {
          font-size: 0.65rem;
          font-weight: 600;
          padding: 0.15rem 0.5rem;
          background: #171717;
          color: #94a3b8;
          border: 1px solid #262626;
          border-radius: 3px;
        }

        .inspector-title-wrap h4 {
          font-size: 1.05rem;
          color: #ffffff;
          margin: 0;
        }

        .inspector-status {
          font-size: 0.7rem;
          color: #10b981;
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }

        .inspector-desc {
          font-size: 0.85rem;
          color: var(--text-muted);
          line-height: 1.5;
          margin-bottom: 1rem;
        }

        .inspector-specs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 0.75rem;
        }

        .spec-box {
          background: #0d0d0d;
          border: 1px solid var(--border-color);
          border-radius: 4px;
          padding: 0.65rem 0.85rem;
          display: flex;
          flex-direction: column;
          gap: 0.3rem;
        }

        .spec-lbl {
          font-size: 0.62rem;
          color: var(--text-dim);
          letter-spacing: 0.04em;
        }

        .spec-val {
          font-size: 0.76rem;
          color: #e2e8f0;
          line-height: 1.35;
        }
      `}</style>
    </div>
  );
};
